import React, { useState, useRef } from "react";
import { useHistory } from "react-router-dom";
import PostBrandLabel from "components/PostBrandLabel/PostBrandLabel";
import { Draggable } from "react-beautiful-dnd";
import clock from "assets/images/icons/clock.svg";
import money from "assets/images/icons/money.svg";
import dots from "assets/images/icons/3dots.png";
import { MDBDropdown, MDBDropdownToggle, MDBDropdownMenu } from "mdbreact";
import { useSelector, useDispatch } from "react-redux";
import { ALL_PERMISSIONS } from "constants/permissionsConstants";
import { createFeatureRequest } from "api/featureRequests/createFeatureRequest";
import {
  setFeatureRequest,
  setBrands,
  updateBrandData,
} from "actions/user";
import { Modal } from "antd";
import { RequestCard } from "pages/configuration/requestCard";
import { ClockCircleOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { eventTrack } from "helpers/ga4Helper";
import { editBrandPut } from "api/brands/updateBrand";
import addNotification from "helpers/notify";
import { companyAdminEditBrand } from "actions/company-admin";
import { useHttp } from "hooks/useHttp";
import { PlatformIcon } from "components/SocialMediaBrand/SocialMediaBrand";
import { formatPostTime, PubTypeBadge, StatusBadge as SharedStatusBadge, PostThumb, pickPostThumb, postPlatforms } from "helpers/postDisplay";
import { canDeletePost } from "helpers/postDeletable";
import { archiveControl } from "helpers/postArchive";
import { editPost } from "api/brands/editPost";
import { setPostsForBrand } from "actions/posts";
import { updateIdea } from "api/ideas/addUpdateIdea";
import { setIdeas } from "actions/idea";
import { computeFinalPermissions } from 'helpers/finalPermissions';

// formatDate now defers to the shared helper so kanban renders the
// same time string as list + calendar for the same post. Kept the
// name in place to avoid touching every reference below.
const formatDate = (dateString) => formatPostTime(dateString);

// Trim post/idea text for the card preview. Humans will scan these — too
// much text and they tune out. ~160 chars covers a 2-line clamp; the full
// text is one click away in the edit modal.
const truncate = (s, max = 160) => {
  if (!s) return "";
  const t = String(s).trim().replace(/\s+/g, " ");
  return t.length > max ? t.slice(0, max - 1) + "…" : t;
};

// Pull the first usable thumbnail/video poster. Posts have imageContent
// (string[]) and may have videoContent. Some legacy shapes nested it
// under .image / .images / .url — accept all.
const pickThumb = (b = {}) => {
  if (Array.isArray(b.imageContent) && b.imageContent[0]) return b.imageContent[0];
  if (Array.isArray(b.images) && b.images[0]) return b.images[0]?.url || b.images[0];
  if (b.image) return b.image?.url || b.image;
  return null;
};
const pickVideo = (b = {}) => (Array.isArray(b.videoContent) ? b.videoContent[0] : null);
let numeral = require("numeral");

const KanbanCard = ({
  source,
  brand,
  index,
  isAdmin,
  setModal,
  setBrandToDelete,
  setBrandToUpdate,
  setBrandListToEdit,
  onEdit,
  onArchive,
}) => {
  const { img, name, zipInfo, date, posts, brandNumber } = brand;
  const history = useHistory();
  const dispatch = useDispatch();

  const userInfoRedux = useSelector((s) => s.user.userInfo);
  const usersPermissionRedux = useSelector((s) => s.user.userPermission);
  const companyPermissionRedux = useSelector((s) => s.user.companyPermission);
  const allPermissionRedux = useSelector((s) => s.user.allPermission);
  const FinalPermissions = computeFinalPermissions({
    userInfo: userInfoRedux,
    allPermission: allPermissionRedux,
    companyPermission: companyPermissionRedux,
    userPermission: usersPermissionRedux,
  });
  const featureRequestRedux = useSelector((s) => s.user.featureRequest);
  const postsRedux = useSelector((s) => s.post.posts);
  const ideasRedux = useSelector((s) => s.ideas.ideas);
  const usersRedux = useSelector((s) => s.user.users);

  const [requestType, setRequestType] = useState("");
  const [requestModal, setRequestModal] = useState(false);
  const [requestLoading, setRequestLoading] = useState(false);

  const handleAccess = (item) => {
    setBrandListToEdit(item);
    setModal("Share");
  };
  const handleMembers = (item) => {
    setBrandListToEdit(item);
    setModal("BrandListToEdit");
  };

  const handleHistory = () => {
    history.push(`/personal-area/brands/${brand?._id}/posts`);
    {
      eventTrack(
        "View-History-Action",
        "View-History",
        brand?.title,
        brand?._id,
        false,
        brand?.title
      );
    }
  };

  const handleDelete = (item) => {
    setBrandToDelete(item);
    setModal("DeleteModal");
  };

  const requestAccess = async () => {
    try {
      setRequestLoading(true);
      const res = await createFeatureRequest(userInfoRedux?.id, {
        companyId: userInfoRedux.company,
        permission: requestType,
        userId: userInfoRedux.id,
      });
      if (res) {
        setRequestLoading(false);
        dispatch(
          setFeatureRequest(res?.map((feature) => feature?.requestedFeature))
        );
      }
    } catch (e) {
      setRequestLoading(false);
    }
  };

  const form = new FormData();

  const { loading, request: editPostAPI } = useHttp({
    requestCallback: (payload) => editPost(payload),
    onLoad: (res) => {
      addNotification("Post updated successfully", "success");
      const clonePostsArr = [...postsRedux];
      const i = clonePostsArr.findIndex((x) => x._id === res._id);
      clonePostsArr[i] = { ...clonePostsArr[i], favourite: res?.favourite };
      dispatch(setPostsForBrand(clonePostsArr));
    },
    onError: (res) => {
      // addNotification(res.message, "danger");
    },
  });

  const { loading: ideaLoading, request: editIdeaAPI } = useHttp({
    requestCallback: (payload) => updateIdea(payload),
    onLoad: (res) => {
      const ideasCloneArray = [...ideasRedux];
      const i = ideasCloneArray.findIndex((x) => x._id === res._id);
      ideasCloneArray[i] = { ...ideasCloneArray[i], favourite: res?.favourite };
      dispatch(setIdeas(ideasCloneArray));
      addNotification("Idea updated successfully", "success");
    },
    onError: (res) => {
      addNotification(res.err ?? "Something went wrong please connect with support", "danger");
    },
  });

  const toggleFavorite = (value) => {
    if (source === 'post') {
      return editPostAPI({
        id: brand?._id,
        body: { favourite: !value },
      });
    } else {
      return editIdeaAPI({
        _id: brand?._id,
        favourite: !value
      })
    }
  };

  // Clicking anywhere on the card body opens the edit/preview modal —
  // not just the dropdown's "Edit" item. The favorite star + dots menu
  // (and the expandable per-platform details) stop propagation so they
  // keep their own behavior.
  const handleCardOpen = () => {
    const perm = source === 'post' ? ALL_PERMISSIONS.POST_EDIT : ALL_PERMISSIONS.IDEA_EDIT;
    if (FinalPermissions?.includes(perm)) {
      onEdit(brand);
    } else {
      setRequestType(perm);
      setRequestModal(true);
    }
  };

  return (
    <>
      <Draggable draggableId={brand._id} index={index}>
        {(provided, snapshot) => (
          <div
            className={`kanban-card ${snapshot.isDragging ? "dragging" : ""}`}
            {...provided.draggableProps}
            {...provided.dragHandleProps}
            ref={provided.innerRef}
          >
            <div onClick={handleCardOpen} style={{ cursor: "pointer" }}>
              {/* <p className="kanban-card-brandNo">
                Brand No :{" "}
                {brand?.brandNumber ? brand?.brandNumber : "N/A"}
                {brand?.isParent && <i className="fa fa-users cursor-pointer" name="Parent Brand" />}
              </p> */}
              <div className="card-title">
                <p name={brand.title ?? 'Untitled Post'}>
                  {source === 'post' && <PostBrandLabel brandId={brand.brand_id} brandName={brand.brandName} />}
                  {brand.title ?? 'Untitled Post'}
                </p>
                <div className="d-flex align-items-center" onClick={(e) => e.stopPropagation()}>
                  {loading ? (
                    <i className="fa fa-spinner small-spinner" />
                  ) : (
                    <div
                      className="cursor-pointer"
                      onClick={() => {toggleFavorite(brand?.favourite)
                        {
                          eventTrack(
                            "Toggle-Fav-Action",
                            "Toggle-Fav",
                            userInfoRedux?.name,
                            "+1",
                            false,
                            "+1"
                          );
                        }
                      }
                      }
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="19"
                        height="19"
                        viewBox="0 0 19 19"
                        fill="none"
                      >
                        <g clipPath="url(#clip0_426_6573)">
                          <rect
                            width="18"
                            height="18"
                            transform="translate(0.455078 0.45459)"
                            fill="white"
                          />
                          <path
                            d="M9.45602 14.1496L4.16593 17.1107L5.34743 11.1645L0.896484 7.04844L6.91676 6.33463L9.45602 0.82959L11.9952 6.33463L18.0155 7.04844L13.5646 11.1645L14.7461 17.1107L9.45602 14.1496Z"
                            fill={brand?.favourite ? "#FFBF1B" : "#ddd"}
                          />
                        </g>
                        <defs>
                          <clipPath id="clip0_426_6573">
                            <rect
                              width="18"
                              height="18"
                              fill="white"
                              transform="translate(0.455078 0.45459)"
                            />
                          </clipPath>
                        </defs>
                      </svg>
                    </div>
                  )}

                  <MDBDropdown dropright className="ml-2">
                    <MDBDropdownToggle className="dots-btn">
                      <img src={dots} className="dots px-2 cursor-pointer" />
                    </MDBDropdownToggle>
                    <MDBDropdownMenu className="w-100 border-none">
                      <div className="card-body-help">
                        <ul className="siderbar-inner-ul">
                          {/* <li
                            onClick={() => {
                              FinalPermissions?.includes(
                                ALL_PERMISSIONS.SHARE_brand_ADD
                              )
                                ? handleAccess(brand)
                                : setRequestModal(true);
                              setRequestType(ALL_PERMISSIONS.SHARE_brand_ADD);
                            }}
                          >
                            Access
                          </li>
                          <li
                            onClick={() => {
                              FinalPermissions?.includes(
                                ALL_PERMISSIONS.ASSIGN_brand_ADD
                              )
                                ? handleMembers(brand)
                                : setRequestModal(true);

                              setRequestType(
                                ALL_PERMISSIONS.ASSIGN_brand_ADD
                              );
                            }}
                          >
                            Members
                          </li>
                          <li
                            onClick={() => {
                              FinalPermissions?.includes(
                                ALL_PERMISSIONS.ESTIMATION_VIEW
                              )
                                ? handleHistory(brand)
                                : setRequestModal(true);

                              setRequestType(ALL_PERMISSIONS.ESTIMATION_VIEW);
                            }}
                          >
                            History
                          </li>
                          {brand?.zipInfo?.paths.length > 0 && (
                            <li
                              onClick={() => {
                                history.push(
                                  `/personal-area/brands/model-view/${brand?._id}`
                                );
                                {
                                  eventTrack(
                                    "View-Model-Action",
                                    "View-Model",
                                    brand?.name,
                                    brand?._id,
                                    false,
                                    brand?.name
                                  );
                                }
                              }}
                            >
                              View Model
                            </li>
                          )} */}

                          <li
                            onClick={() => {
                              FinalPermissions?.includes(
                                source === 'post' ? ALL_PERMISSIONS.POST_EDIT : ALL_PERMISSIONS.IDEA_EDIT
                              )
                                ? onEdit(brand)
                                : setRequestModal(true);

                              setRequestType(source === 'post' ? ALL_PERMISSIONS.POST_EDIT : ALL_PERMISSIONS.IDEA_EDIT);
                            }}
                          >
                            Edit
                          </li>
                          {/* Archive / un-archive — same control as the
                              Calendar + List views (helpers/postArchive).
                              Post-only; wired only when the parent passes
                              onArchive. Sends a live post to the Archive
                              shelf, or an archived post back to "awaiting
                              approval". */}
                          {source === 'post' && onArchive && (
                            <li
                              onClick={() => onArchive(brand)}
                            >
                              {archiveControl(brand).archived ? 'Send to awaiting approval' : 'Archive'}
                            </li>
                          )}
                          {/* Hide Delete in the menu once a post has
                              actually gone out — deleting the row
                              doesn't unpublish on the platform, just
                              hides the audit trail. Backend enforces
                              the same rule (returns 409); this is the
                              UI half. Ideas don't have a posted state
                              so the option always shows for them. */}
                          {(source !== 'post' || canDeletePost(brand)) && (
                            <li
                              onClick={() => {
                                FinalPermissions?.includes(
                                  source === 'post' ? ALL_PERMISSIONS.POST_DELETE : ALL_PERMISSIONS.IDEA_DELETE
                                )
                                  ? handleDelete(brand)
                                  : setRequestModal(true);

                                setRequestType(source === 'post' ? ALL_PERMISSIONS.POST_DELETE : ALL_PERMISSIONS.IDEA_DELETE);
                              }}
                            >
                              Delete
                            </li>
                          )}
                        </ul>
                      </div>
                    </MDBDropdownMenu>
                  </MDBDropdown>
                </div>
              </div>
              {(() => {
                const thumb = pickPostThumb(brand);
                if (!thumb) return null;
                return (
                  <div className="card-thumb">
                    <PostThumb post={brand} className="card-thumb-img" alt={brand.title || ""} />
                    {thumb.isVideo && <span className="card-thumb-badge">Video</span>}
                  </div>
                );
              })()}
              {/* Status pill at the top — kanban also buckets by
                  status (column position), but the pill gives the
                  exact lifecycle string (Pending / Scheduled / Posted /
                  Posting failed / etc.) so the user doesn't have to
                  count columns. Same widget the calendar + list use. */}
              <div style={{ marginBottom: 6 }}>
                <SharedStatusBadge item={brand} source={source} size={10} />
              </div>
              {/* Wrap text instead of single-line truncate. WebKit
                  line-clamp keeps cards from growing unboundedly when
                  copy is very long. */}
              <div className="card-text" style={{
                display: '-webkit-box', WebkitLineClamp: 5, WebkitBoxOrient: 'vertical',
                overflow: 'hidden', whiteSpace: 'normal', wordBreak: 'break-word', lineHeight: 1.35,
              }}>
                {source === 'post'
                  ? (brand.textContent || brand.description || '')
                  : (brand.description || brand.prompt || '')}
              </div>
              {/* Per-platform copy variants — the same post adjusted per platform.
                  Collapsed by default to keep the card compact; expanding doesn't
                  trigger the card's own click. Only shown when there are 2+ variants
                  (a single-platform post's variant is just the caption above). */}
              {source === 'post' && brand.platformContent && Object.keys(brand.platformContent).length > 1 && (
                <details
                  className="card-platform-variants"
                  style={{ marginTop: 4 }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <summary style={{ fontSize: 11, color: '#2563eb', cursor: 'pointer', listStyle: 'none' }}>
                    📝 Per-platform copy ({Object.keys(brand.platformContent).length})
                  </summary>
                  <div style={{ marginTop: 4 }}>
                    {Object.entries(brand.platformContent).map(([plat, copy]) => (
                      <div key={plat} style={{ marginTop: 6, fontSize: 11, borderLeft: '2px solid #e5e7eb', paddingLeft: 6 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontWeight: 600, textTransform: 'capitalize' }}>
                          <PlatformIcon platform={plat} /> {plat}
                        </div>
                        <div style={{ color: '#525252', whiteSpace: 'pre-wrap', wordBreak: 'break-word', lineHeight: 1.35 }}>{copy}</div>
                      </div>
                    ))}
                  </div>
                </details>
              )}
              {source === 'post' ?
                (<div className="card-detail-container">
                  <div className="item">
                    <div>
                      {/* Use the shared resolver instead of reading raw fields.
                          The old `targetedSocialAccounts || socialPlatforms`
                          fallback broke for auto-generated posts whose
                          targetedSocialAccounts is an empty array (truthy →
                          never fell back to socialPlatforms), leaving no logos.
                          postPlatforms() unions both shapes. */}
                      {postPlatforms(brand).map((p, i) =>
                        <PlatformIcon key={i} platform={p} />
                      )}
                    </div>
                  </div>
                  <div className="item">
                    {/* Publication-type indicator with tooltip — icon
                        for text/image/video composite + hover label. */}
                    <PubTypeBadge type={brand.type} size={10} />
                  </div>
                  <div className="item">
                    <div className="d-flex align-items-center">
                      <img src={clock} className="d-icon mr-1" style={{ width: 11, height: 11 }} />
                      {/* Compact time — fits the card row without
                          wrapping; tooltip carries the full timestamp. */}
                      <div style={{ fontSize: 11, color: '#525252', whiteSpace: 'nowrap' }}
                           title={formatPostTime(brand.scheduled_at || brand.updatedAt)}>
                        {formatPostTime(brand.scheduled_at || brand.updatedAt)}
                      </div>
                    </div>
                  </div>
                </div>) : (<div className="card-detail-container">
                  <div className="item card-prompt">
                    Prompt: {truncate(brand.prompt, 90)}
                  </div>
                  <div className="item">
                    <div className="d-flex align-items-center">
                      <img src={clock} className="d-icon mr-1" style={{ width: 11, height: 11 }} />
                      <div style={{ fontSize: 11, color: '#525252', whiteSpace: 'nowrap' }}
                           title={formatPostTime(brand.date || brand.updatedAt)}>
                        {formatPostTime(brand.date || brand.updatedAt)}
                      </div>
                    </div>
                  </div>
                </div>)}
              {provided.placeholder}
            </div>
          </div>
        )}
      </Draggable>

      {/* request access model */}
      <Modal
        name={
          userInfoRedux?.companyAdmin ? "Acquire License" : "Request Access"
        }
        open={requestModal}
        footer={null}
        onCancel={() => setRequestModal(false)}
      >
        <RequestCard
          status={featureRequestRedux?.includes(requestType)}
          loading={requestLoading}
          icon={
            featureRequestRedux?.includes(requestType) ? (
              <ClockCircleOutlined style={{ color: "black" }} />
            ) : (
              <InfoCircleOutlined style={{ color: "black" }} />
            )
          }
          name={
            featureRequestRedux?.includes(requestType)
              ? `Request Already Sent`
              : `Oops, looks like you need to ${
                  userInfoRedux?.companyAdmin
                    ? `Acquire License`
                    : `Request Access`
                }`
          }
          description={
            featureRequestRedux?.includes(requestType)
              ? `Please reach out to your brand Admin`
              : `Elevate your experience with exclusive futures, early releases, and premium features.`
          }
          buttonText={
            userInfoRedux?.companyAdmin ? "Acquire License" : "Request Access"
          }
          buttonHandler={() => requestAccess()}
        />
      </Modal>
    </>
  );
};

export default KanbanCard;
